import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './DependencyGraph.css';
import { DependencyGraphProps, DependencyArc, WordNode } from './types';

/** Estimate rendered width of a word in px (approximation for SVG text) */
const estimateWordWidth = (word: string): number => {
  let width = 0;
  for (const ch of word) {
    if (/[mwMW]/.test(ch)) width += 12;
    else if (/[iltfjr!|.,;:'()]/.test(ch)) width += 5;
    else if (/[A-Z]/.test(ch)) width += 10;
    else width += 8;
  }
  return Math.max(width, 30);
};

/** Estimate label width in SVG units */
const estimateLabelWidth = (label: string): number => {
  let w = 0;
  for (const ch of label) {
    w += ch.charCodeAt(0) > 0x2e80 ? 11 : 6.5;
  }
  return w;
};

/**
 * Fixed role palette — warm, brand-aligned colours.
 * Ordered so that more specific tokens are matched before generic ones
 * (e.g. "auxiliary" and "adverb" must be checked before "verb", which is
 * a substring of both; "助動詞" before "動詞" for the Chinese fallback).
 */
const ROLE_PALETTE: Array<{
  en: string[];
  zh: string[];
  color: string;
}> = [
  { en: ['preposition'], zh: ['介係詞', '介詞'], color: '#14b8a6' },
  { en: ['interjection'], zh: ['感嘆詞', '嘆詞'], color: '#f59e0b' },
  { en: ['conjunction'], zh: ['連接詞', '連詞', '连接词', '连词'], color: '#64748b' },
  { en: ['auxiliary'], zh: ['助動詞', '助动词'], color: '#7c3aed' },
  { en: ['adjective'], zh: ['形容詞', '形容词'], color: '#ec4899' },
  { en: ['adverb'], zh: ['副詞', '副词'], color: '#db2777' },
  { en: ['pronoun'], zh: ['代名詞', '代詞', '代名词', '代词'], color: '#0ea5e9' },
  { en: ['particle'], zh: ['助詞', '助词'], color: '#a855f7' },
  { en: ['article', 'determiner'], zh: ['冠詞', '冠词', '限定詞', '限定词'], color: '#cbd5e1' },
  { en: ['subject'], zh: ['主詞', '主語', '主词', '主语'], color: '#4f7cff' },
  { en: ['object'], zh: ['受詞', '賓語', '受词', '宾语'], color: '#f97316' },
  { en: ['modifier'], zh: ['修飾', '修饰'], color: '#f472b6' },
  { en: ['verb'], zh: ['動詞', '动词'], color: '#9333ea' },
  { en: ['noun'], zh: ['名詞', '名词'], color: '#fb923c' },
];

const DEFAULT_ROLE_COLOR = '#64748b';

/** Resolve a stable brand colour for a word based on its grammatical role. */
const getRoleColor = (word: WordNode): string => {
  const roleEn = (word.role || '').toLowerCase();
  const roleZh = word.roleZh || '';
  for (const entry of ROLE_PALETTE) {
    if (entry.en.some(k => roleEn.includes(k)) || entry.zh.some(k => roleZh.includes(k))) {
      return entry.color;
    }
  }
  return DEFAULT_ROLE_COLOR;
};

/** Visual tier for an arc based on highlight state */
type ArcTier = 'background' | 'partial' | 'historical' | 'fresh';

const PRIMARY_COLOR = '#667eea';

/** Arc connectors with 4-tier rendering and label collision detection */
const ArcArcsLayer: React.FC<{
  arcs: DependencyArc[];
  words: WordNode[];
  positions: number[];
  arcBaseY: number;
  isHL: (id: string) => boolean;
  isFresh: (id: string) => boolean;
  getColor: (id: string) => string;
}> = ({ arcs, words, positions, arcBaseY, isHL, isFresh, getColor }) => {
  // Pre-compute arc geometry
  const arcData = useMemo(() => {
    return arcs.map((arc, i) => {
      const fi = words.findIndex(w => w.id === arc.from);
      const ti = words.findIndex(w => w.id === arc.to);
      if (fi === -1 || ti === -1) return null;
      const fromX = positions[fi];
      const toX = positions[ti];
      const dist = Math.abs(ti - fi);
      // Logarithmic height: short arcs get reasonable lift, long arcs keep distinguishable depth
      const arcH = Math.min(30 + Math.log2(dist + 1) * 28, 160);
      const controlY = arcBaseY - arcH;
      const midX = (fromX + toX) / 2;
      const curvePeakY = (arcBaseY + controlY) / 2;
      const path = `M ${fromX} ${arcBaseY} Q ${midX} ${controlY} ${toX} ${arcBaseY}`;
      return { arc, index: i, fromX, toX, controlY, midX, curvePeakY, path, labelW: estimateLabelWidth(arc.label) };
    }).filter(Boolean) as Array<{
      arc: DependencyArc; index: number; fromX: number; toX: number;
      controlY: number; midX: number; curvePeakY: number; path: string; labelW: number;
    }>;
  }, [arcs, words, positions, arcBaseY]);

  // Classify each arc into a visual tier
  const tieredArcs = useMemo(() => {
    return arcData.map(d => {
      const fromHL = isHL(d.arc.from);
      const toHL = isHL(d.arc.to);
      let tier: ArcTier;
      if (fromHL && toHL) {
        // "Fresh" if either endpoint is freshly added in the current step
        tier = (isFresh(d.arc.from) || isFresh(d.arc.to)) ? 'fresh' : 'historical';
      } else if (fromHL || toHL) {
        tier = 'partial';
      } else {
        tier = 'background';
      }
      return { ...d, tier };
    });
  }, [arcData, isHL, isFresh]);

  // Resolve label collisions for visible (fresh + historical) labels
  const labelPositions = useMemo(() => {
    const LABEL_H = 14;
    const PAD_X = 6;
    const PAD_Y = 2;

    const visibleLabels = tieredArcs
      .filter(d => d.tier === 'fresh' || d.tier === 'historical')
      .map(d => ({
        index: d.index,
        x: d.midX,
        y: d.curvePeakY - 5,
        w: d.labelW,
        h: LABEL_H,
      }));

    visibleLabels.sort((a, b) => a.x - b.x);

    for (let i = 0; i < visibleLabels.length; i++) {
      for (let j = i + 1; j < visibleLabels.length; j++) {
        const a = visibleLabels[i];
        const b = visibleLabels[j];
        const xOverlap = Math.abs(a.x - b.x) < (a.w + b.w) / 2 + PAD_X;
        if (!xOverlap) continue;
        const yOverlap = Math.abs(a.y - b.y) < LABEL_H + PAD_Y;
        if (yOverlap) {
          if (b.y >= a.y) {
            b.y = a.y - LABEL_H - PAD_Y;
          } else {
            a.y = b.y - LABEL_H - PAD_Y;
          }
        }
      }
    }

    const map = new Map<number, { x: number; y: number }>();
    visibleLabels.forEach(l => map.set(l.index, { x: l.x, y: l.y }));
    return map;
  }, [tieredArcs]);

  return (
    <g>
      {/* Pass 1: background — barely visible, hint of structure */}
      {tieredArcs.filter(d => d.tier === 'background').map(d => (
        <path
          key={`bg-${d.index}`}
          d={d.path}
          fill="none"
          stroke="#cbd5e1"
          strokeWidth={1.2}
          opacity={0.22}
          style={{ transition: 'all 0.25s' }}
        />
      ))}

      {/* Pass 2: partial preview — one endpoint highlighted, dashed lookahead */}
      {tieredArcs.filter(d => d.tier === 'partial').map(d => {
        const color = getColor(d.arc.to);
        return (
          <path
            key={`pt-${d.index}`}
            d={d.path}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            opacity={0.6}
            strokeDasharray="3 4"
            style={{ transition: 'all 0.25s' }}
          />
        );
      })}

      {/* Pass 3: historical — both endpoints lit but not freshly added this step */}
      {tieredArcs.filter(d => d.tier === 'historical').map(d => {
        const color = getColor(d.arc.to);
        const labelPos = labelPositions.get(d.index);
        return (
          <g key={`ht-${d.index}`}>
            <path
              d={d.path}
              fill="none"
              stroke={color}
              strokeWidth={2}
              opacity={0.65}
              markerEnd="url(#arc-arrow)"
              style={{ transition: 'all 0.25s' }}
            />
            {labelPos && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                className="dg-arc-label dg-arc-label-historical"
                fill={color}
                opacity={0.85}
              >
                {d.arc.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Pass 4: fresh — current step's focus, single brand color, bold */}
      {tieredArcs.filter(d => d.tier === 'fresh').map(d => {
        const labelPos = labelPositions.get(d.index);
        return (
          <g key={`fr-${d.index}`}>
            <path
              d={d.path}
              fill="none"
              stroke={PRIMARY_COLOR}
              strokeWidth={3.5}
              markerEnd="url(#arc-arrow)"
              style={{ transition: 'all 0.25s' }}
            />
            {labelPos && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                className="dg-arc-label dg-arc-label-fresh"
                fill={PRIMARY_COLOR}
              >
                {d.arc.label}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
};

const DependencyGraph: React.FC<DependencyGraphProps> = ({
  data,
  showControls = true,
  showLegend = true,
  autoPlay = false,
  defaultDuration = 3500,
}) => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [highlightedNodes, setHighlightedNodes] = useState<Set<string>>(new Set());
  const [containerWidth, setContainerWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation('learning');
  const { words, arcs, animationSteps } = data;

  // Observe container width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Priority 1: role-based colour lookup (ignores DB-provided word.color)
  const nodeColors = useMemo(() => {
    const colors: Record<string, string> = {};
    words.forEach(w => { colors[w.id] = getRoleColor(w); });
    return colors;
  }, [words]);

  // Dynamic word positions
  const { positions, svgWidth, needsScroll } = useMemo(() => {
    const GAP = 16;
    const PADDING = 40;
    const wordWidths = words.map(w => estimateWordWidth(w.word));
    const naturalWidth = wordWidths.reduce((sum, w) => sum + w, 0) + GAP * (words.length - 1) + PADDING * 2;
    const availableWidth = Math.max(containerWidth - 32, 320);
    const useWidth = Math.max(naturalWidth, availableWidth);
    const scale = useWidth / naturalWidth;

    const pos: number[] = [];
    let x = PADDING;
    words.forEach((_, i) => {
      const w = wordWidths[i] * scale;
      pos.push(x + w / 2);
      x += w + GAP * scale;
    });

    return {
      positions: pos,
      svgWidth: useWidth,
      needsScroll: naturalWidth > availableWidth * 1.02,
    };
  }, [words, containerWidth]);

  // SVG dimensions — log2 arc heights, generous top buffer for collision-pushed labels
  const { svgHeight, wordY, arcBaseY } = useMemo(() => {
    let maxH = 30;
    arcs.forEach(arc => {
      const fi = words.findIndex(w => w.id === arc.from);
      const ti = words.findIndex(w => w.id === arc.to);
      if (fi === -1 || ti === -1) return;
      const h = Math.min(30 + Math.log2(Math.abs(ti - fi) + 1) * 28, 160);
      if (h > maxH) maxH = h;
    });
    const TOP_BUFFER = 32; // room for labels pushed upward by collision resolver
    const baseY = TOP_BUFFER + maxH + 20;
    return { svgHeight: baseY + 34, wordY: baseY + 22, arcBaseY: baseY - 5 };
  }, [words, arcs]);

  // Compute "freshly added" nodes — those in current step but not in previous step.
  // Step 0 treats all nodeIds as fresh.
  const freshNodeIds = useMemo(() => {
    if (currentStep < 0 || currentStep >= animationSteps.length) return new Set<string>();
    const current = new Set(animationSteps[currentStep].nodeIds || []);
    if (currentStep === 0) return current;
    const prev = new Set(animationSteps[currentStep - 1].nodeIds || []);
    const fresh = new Set<string>();
    current.forEach(id => { if (!prev.has(id)) fresh.add(id); });
    // If step accumulation isn't used (reset model), treat all as fresh
    return fresh.size === 0 ? current : fresh;
  }, [currentStep, animationSteps]);

  const playAnimation = useCallback(() => {
    setIsPlaying(true);
    setCurrentStep(0);
  }, []);

  const resetAnimation = useCallback(() => {
    setIsPlaying(false);
    setCurrentStep(-1);
    setHighlightedNodes(new Set());
  }, []);

  const goToStep = useCallback((step: number) => {
    setIsPlaying(false);
    if (step < 0) {
      setCurrentStep(-1);
      setHighlightedNodes(new Set());
    } else if (step >= animationSteps.length) {
      setCurrentStep(animationSteps.length - 1);
      setHighlightedNodes(new Set(animationSteps[animationSteps.length - 1].nodeIds));
    } else {
      setCurrentStep(step);
      setHighlightedNodes(new Set(animationSteps[step].nodeIds));
    }
  }, [animationSteps]);

  const prevStep = useCallback(() => { goToStep(currentStep - 1); }, [currentStep, goToStep]);
  const nextStep = useCallback(() => { goToStep(currentStep < 0 ? 0 : currentStep + 1); }, [currentStep, goToStep]);

  useEffect(() => {
    if (!isPlaying || currentStep < 0) return;
    if (currentStep >= animationSteps.length) {
      setIsPlaying(false);
      setCurrentStep(-1);
      setHighlightedNodes(new Set());
      return;
    }
    const step = animationSteps[currentStep];
    setHighlightedNodes(new Set(step.nodeIds));
    const timer = setTimeout(() => { setCurrentStep(prev => prev + 1); }, step.duration || defaultDuration);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, animationSteps, defaultDuration]);

  // Auto-scroll horizontally so the current step's focus is centered
  useEffect(() => {
    if (!needsScroll || !graphRef.current) return;
    if (currentStep < 0 || freshNodeIds.size === 0) return;

    let sumX = 0;
    let count = 0;
    arcs.forEach(arc => {
      if (!freshNodeIds.has(arc.from) && !freshNodeIds.has(arc.to)) return;
      const fi = words.findIndex(w => w.id === arc.from);
      const ti = words.findIndex(w => w.id === arc.to);
      if (fi < 0 || ti < 0) return;
      sumX += (positions[fi] + positions[ti]) / 2;
      count++;
    });

    // Fall back to centering on the first fresh node if no fresh arcs
    if (count === 0) {
      freshNodeIds.forEach(id => {
        const i = words.findIndex(w => w.id === id);
        if (i >= 0) { sumX += positions[i]; count++; }
      });
    }
    if (count === 0) return;

    const avgX = sumX / count;
    const container = graphRef.current;
    const targetScroll = avgX - container.clientWidth / 2;
    container.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
  }, [currentStep, freshNodeIds, needsScroll, arcs, words, positions]);

  const getNodeColor = (nodeId: string): string => nodeColors[nodeId] || DEFAULT_ROLE_COLOR;
  const isNodeHighlighted = useCallback((nodeId: string): boolean => highlightedNodes.has(nodeId), [highlightedNodes]);
  const isNodeFresh = useCallback((nodeId: string): boolean => freshNodeIds.has(nodeId), [freshNodeIds]);

  // Priority 1: legend dedupes by the stable role colour, not the
  // DB-provided word.color field.
  const legendItems = useMemo(() => {
    const seen = new Map<string, { color: string; roleZh: string; role: string }>();
    words.forEach(w => {
      const color = getRoleColor(w);
      if (!seen.has(color)) {
        seen.set(color, { color, roleZh: w.roleZh, role: w.role });
      }
    });
    return Array.from(seen.values());
  }, [words]);

  return (
    <div className="dependency-graph-container" ref={containerRef}>
      {/* Compact controls: one row */}
      {showControls && (
        <div className="dg-controls">
          <button
            className="dg-step-btn"
            onClick={prevStep}
            disabled={currentStep < 0}
            title={t('dependencyGraph.previousStep')}
            aria-label={t('dependencyGraph.previousStep')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
          </button>

          <button
            className={`dg-play-btn ${isPlaying ? 'dg-play-btn--active' : ''}`}
            onClick={isPlaying ? resetAnimation : playAnimation}
            aria-label={isPlaying ? t('dependencyGraph.stop') : t('dependencyGraph.autoPlay')}
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            )}
          </button>

          <div className="dg-step-indicator">
            {currentStep >= 0
              ? t('dependencyGraph.step', { current: currentStep + 1, total: animationSteps.length })
              : t('dependencyGraph.clickToStart')
            }
          </div>

          <button
            className="dg-step-btn"
            onClick={nextStep}
            disabled={currentStep >= animationSteps.length - 1}
            title={t('dependencyGraph.nextStep')}
            aria-label={t('dependencyGraph.nextStep')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
          </button>
        </div>
      )}

      {/* Explanation — fixed height to prevent layout shift */}
      <div className={`dg-explanation ${currentStep >= 0 && currentStep < animationSteps.length ? 'show' : ''}`}>
        {currentStep >= 0 && currentStep < animationSteps.length && (
          <p>{animationSteps[currentStep].explanation}</p>
        )}
      </div>

      {/* Graph (single SVG view, horizontal scroll on overflow) */}
      <div
        ref={graphRef}
        className={`dg-graph ${needsScroll ? 'dg-graph--scrollable' : ''}`}
      >
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="dg-svg"
          style={needsScroll ? { width: `${svgWidth}px`, minWidth: `${svgWidth}px`, height: `${svgHeight}px`, maxWidth: 'none' } : undefined}
        >
          {/* Arrowhead marker — inherits arc stroke color */}
          <defs>
            <marker id="arc-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="strokeWidth">
              <path d="M 0 0 L 6 4 L 0 8" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </marker>
          </defs>

          <ArcArcsLayer
            arcs={arcs}
            words={words}
            positions={positions}
            arcBaseY={arcBaseY}
            isHL={isNodeHighlighted}
            isFresh={isNodeFresh}
            getColor={getNodeColor}
          />

          {/* Words + dots */}
          <g className="dg-words">
            {words.map((word, index) => {
              const x = positions[index];
              const highlighted = isNodeHighlighted(word.id);
              const fresh = isNodeFresh(word.id);
              return (
                <g key={word.id}>
                  <circle
                    cx={x}
                    cy={arcBaseY}
                    r={highlighted ? 5 : 2.5}
                    fill={highlighted ? getNodeColor(word.id) : '#cbd5e1'}
                    className={fresh ? 'dg-pulse' : ''}
                  />
                  <text
                    x={x}
                    y={wordY}
                    textAnchor="middle"
                    className={`dg-word ${highlighted ? 'dg-highlighted' : ''}`}
                    fill={highlighted ? getNodeColor(word.id) : '#475569'}
                  >
                    {word.word}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="dg-legend">
          {legendItems.map((item, index) => (
            <div key={index} className="dg-legend-item">
              <span className="dg-legend-dot" style={{ background: item.color }} />
              <span>{item.roleZh} ({item.role})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DependencyGraph;
